import type { MultiplayerStatus } from '../store/multiplayer-store';

/** Quel poco di stato che serve per decidere se si e' rimasti su una damiera vuota. */
export interface StatoDelRientro {
  /** Schermata corrente dell'applicazione. */
  screen: string;
  status: MultiplayerStatus;
  roomId: string | null;
  /** C'e' ancora una posizione disegnata sotto? (`boardState !== null`) */
  haUnaDamiera: boolean;
}

/**
 * Il rientro non e' riuscito e la partita e' stata RILASCIATA: si deve uscire
 * dalla schermata di gioco.
 *
 * Quando `checkActiveGame` non recupera la partita — identita' non ritrovata,
 * o server che non risponde — lo store azzera stanza e damiera e torna a
 * 'connected'. Senza questa uscita il giocatore resta sulla schermata di gioco
 * con una damiera che non e' piu' di nessuna partita: le mosse non arrivano da
 * nessuna parte, non c'e' fine partita, non c'e' pannello di rivincita e
 * nemmeno un `onLeave` da premere. La scelta di rilasciare invece di
 * aggrapparsi vale solo SE porta fuori davvero.
 *
 * Le esclusioni non sono cautele generiche, sono casi veri:
 *
 * - `'playing'` con la stanza ancora in mano e' anche la pausa fra le due
 *   manche di una partita a due: li' i giocatori restano dentro di proposito, e
 *   sbalzarli fuori farebbe perdere il ritorno a chi non ha fatto niente;
 * - `'ended'` e' la schermata di fine partita, con la rivincita;
 * - stanza gia' assegnata ma damiera non ancora arrivata e' la finestra del
 *   matchmaking, cioe' qualcuno che ha appena trovato un avversario.
 */
export function laPartitaOnlineEStataRilasciata(stato: StatoDelRientro): boolean {
  if (stato.screen !== 'online-game') return false;
  if (stato.roomId !== null || stato.haUnaDamiera) return false;
  return stato.status === 'connected' || stato.status === 'disconnected';
}

/**
 * Si sta giocando una partita di TORNEO da una schermata che non e' la sua.
 *
 * La navigazione dei tornei (`App`) porta a `tournament-game` solo quando lo
 * schermo non e' gia' una schermata di partita — `online-game`, `arena-game`,
 * `tournament-game`. La cautela ha una ragione: non strappare via chi sta
 * giocando. Ma ha un buco, ed e' quello per cui esiste questa funzione: se per
 * qualunque strada si finisce su `online-game` mentre il torneo dice
 * 'playing', quella stessa cautela impedisce PER SEMPRE la correzione, e si
 * resta sulla schermata sbagliata per tutto il torneo.
 *
 * `tournamentStatus === 'playing'` vuol dire che la partita in corso E' quella
 * del torneo: l'unica schermata legittima e' `tournament-game`, e ogni altra
 * schermata di partita e' un errore da correggere, non un giocatore da
 * rispettare.
 *
 * Restano fuori `arena-game` e le schermate che non sono di partita: la prima
 * perche' arena e torneo non possono essere in partita insieme e, se
 * succedesse, chi ha una partita d'arena davanti agli occhi la sta giocando
 * davvero; le seconde perche' le governa gia' il ramo ordinario.
 */
export function laPartitaDiTorneoEInUnaSchermataSbagliata(
  screen: string,
  tournamentStatus: string,
): boolean {
  return tournamentStatus === 'playing' && screen === 'online-game';
}

/** Quel poco di stato del TORNEO che dice se si e' rimasti su una damiera morta. */
export interface StatoDelRientroDiTorneo {
  /** Schermata corrente dell'applicazione. */
  screen: string;
  /** La stanza della partita di torneo (`currentRoomId` dello store). */
  currentRoomId: string | null;
  /** C'e' ancora una posizione nello store del torneo? (`boardState !== null`) */
  haUnaDamiera: boolean;
  /** L'esito a schermo: finche' c'e', c'e' un riepilogo da leggere. */
  gameResult: string | null;
}

/**
 * La partita di TORNEO e' stata rilasciata: si deve uscire dalla sua schermata.
 *
 * E' la seconda meta' del difetto G6 (`docs/AUDIT-TORNEI-DATI-2026-09-24.md`).
 * Dopo un riavvio del server — e succede a ogni deploy — la stanza non esiste
 * piu', perche' le stanze vivono in RAM: il rientro riceve un «no» autorevole e
 * `rilasciaPartita` azzera stanza, damiera, orologio ed esito. Da li' nessun
 * ramo della navigazione portava via: quello che entra pretende
 * 'playing'/'match-found', quello che esce pretende 'finished', e `onGameEnd` —
 * l'unica uscita che quella schermata sa guidare — la chiama il riepilogo di
 * fine partita, che il rilascio ha appena cancellato.
 *
 * Il giocatore restava a guardare una posizione che SEMBRA una partita:
 * `TournamentGame` travasa la damiera nello store locale solo quando ce n'e'
 * una, quindi azzerarla di qua non cancella quella gia' disegnata di la'. Con
 * l'orologio fermo e i bottoni muti, e senza nemmeno la domanda di conferma
 * all'uscita — `laPartitaDellaSchermataEInCorso` (utils/partita-in-corso.ts)
 * considera quella schermata «in partita» solo con lo stato 'playing'.
 *
 * Le tre condizioni sono tre esclusioni, e ognuna e' uno stato VERO di quella
 * schermata:
 *
 * - **stanza a nullo** tiene fuori la finestra fra l'accoppiamento e
 *   `game:start`: `setTournamentMatch` scrive la stanza e lascia la damiera
 *   vuota, e `confirmMatch` passa a 'playing' prima che la posizione arrivi.
 *   Li' si sta per giocare, non si e' stati rilasciati;
 * - **nessuna damiera** e' la firma del rilascio: nessun'altra strada azzera la
 *   posizione restando su questa schermata;
 * - **nessun esito** protegge il riepilogo di fine partita (non si strappa via
 *   chi sta leggendo punti e variazione di punteggio) e con lui la pausa fra
 *   andata e ritorno. E' anche perche' non serve guardare `ritornoInArrivo`:
 *   fra le due manche l'esito e' pieno, e `rilasciaPartita` esce da se'.
 *
 * Lo `status` NON si guarda: con la stanza a nullo gli stati «di partita» sono
 * gia' impossibili, e non guardarlo rende questa una rete — da
 * `tournament-game` senza partita sotto si esce, qualunque strada ci abbia
 * portati.
 */
export function laPartitaDiTorneoEStataRilasciata(stato: StatoDelRientroDiTorneo): boolean {
  if (stato.screen !== 'tournament-game') return false;
  return stato.currentRoomId === null && !stato.haUnaDamiera && stato.gameResult === null;
}

/**
 * Dove si va quando una partita viene rilasciata sotto i piedi.
 *
 * La usano i DUE rilasci: quello della partita online e quello della partita di
 * torneo (la seconda meta' di G6, qui sopra). La domanda e' la stessa, e la
 * risposta pure.
 *
 * La destinazione naturale e' la home, e per chi non ha altro e' quella giusta.
 * Ma chi e' iscritto a un torneo ATTIVO non e' «chi non ha altro»: fra un turno
 * e l'altro il suo posto e' la lobby del torneo, ed e' li' che l'accoppiamento
 * del turno successivo lo raggiunge. Mandarlo alla home gli fa perdere il
 * turno — resta a guardare una schermata che non riceve nulla mentre altrove
 * l'orologio della sua partita nuova e' gia' partito.
 *
 * `torneoAttivo` e' vero solo se si e' ISCRITTI a un torneo che sta girando:
 * leggere la scheda di un torneo altrui non basta a dirottare nessuno.
 */
export function doveTornareDopoIlRilascio(torneoAttivo: boolean): 'home' | 'tournament-lobby' {
  return torneoAttivo ? 'tournament-lobby' : 'home';
}

/**
 * A torneo FINITO si va alla scheda: da quale schermata, e dove.
 *
 * E' la regola dell'instradamento dei tornei, estratta da `App.tsx` per poterle
 * mettere accanto la sua gemella — `doveAtterraLaVoceDelTorneo` qui sotto — e
 * provare che quella sia sempre piu' larga di questa. Il comportamento non
 * cambia di una virgola.
 *
 * `gameResult` e' un ingresso per FIX-37: non si strappa via dalla damiera chi
 * sta leggendo punti e variazione di punteggio. Il riepilogo si chiude col suo
 * bottone, e solo allora ci si muove.
 *
 * Anche dalla LOBBY, che col torneo finisce: a torneo chiuso mostrava le stesse
 * tre schede della scheda — classifica, partite, dettagli — cioe' una pagina in
 * piu' che diceva le stesse cose. La scheda e' l'unica pagina del dopo-torneo:
 * quella che si riapre dalla lista, da un link, e che il vincitore lo dice anche
 * a chi alla fine non c'era.
 */
export function doveRimbalzaLaSchermataDelTorneo(stato: {
  screen: string;
  /** `useTournamentStore.status`. */
  tournamentStatus: string;
  /** L'esito a schermo: finche' c'e', c'e' un riepilogo da leggere. */
  gameResult: unknown;
}): 'tournament-detail' | null {
  if (stato.tournamentStatus !== 'finished') return null;
  if (stato.screen === 'tournament-lobby') return 'tournament-detail';
  if (stato.screen === 'tournament-game' && stato.gameResult == null) return 'tournament-detail';
  return null;
}

/**
 * Gli stati in cui il torneo STA GIRANDO, cioe' in cui la lobby e la damiera
 * sono ancora posti dove si puo' atterrare.
 *
 * ⚠️ `TORNEO_IN_CORSO` di `gara-in-corso.ts` ha oggi gli stessi quattro valori e
 * NON va riusata: risponde a un'altra domanda — «sono dentro una gara che
 * gira?», che governa il banner, lo schermo acceso e i bottoni spenti — e le due
 * regole devono poter divergere. E' lo stesso avviso che quel file da' per
 * `laMiaArenaEAttiva`.
 */
const TORNEO_CHE_GIRA = new Set(['waiting', 'round-waiting', 'match-found', 'playing']);

/**
 * La voce di cronologia di una schermata del torneo che non e' piu' un posto:
 * dove si atterra invece, tornandoci col gesto indietro.
 *
 * Non e' la stessa domanda dell'instradamento («da dove porto via chi e' fermo
 * li'?», `App.tsx`, il ramo del torneo finito), ed e' deliberatamente piu'
 * LARGA: quella guarda `'finished'`, questa guarda se il torneo gira. I due
 * motivi sono entrambi misurati:
 *
 * - `'finished'` dura poco. `loadTournament` riscrive `status` a `'viewing'`
 *   SINCRONO al montaggio della scheda (fra gli stati da preservare ci sono solo
 *   'playing' e 'match-found'), e la scheda si monta a ogni passaggio. Legarsi a
 *   quello vorrebbe dire rimontare la lobby di un torneo concluso appena si e'
 *   riaperta la sua pagina una volta.
 * - l'archiviazione del dopo-torneo (`useArchiviazioneTorneo`) porta lo stato a
 *   `'browsing'`: li' la lobby e' ancora meno un posto, e lo store non ha
 *   nemmeno piu' i dati per disegnarla.
 *
 * Deve pero' valere l'invariante, e un test la fissa: **tutto quello che
 * l'instradamento rimbalzerebbe, questa lo rimappa**. Se le due divergessero
 * tornerebbe il rimbalzo, cioe' una voce di cronologia che si riscrive da se'.
 *
 * Due schermate e non una: il ramo del torneo finito lascia in cronologia anche
 * una voce `tournament-game` — e' l'unica uscita dalla damiera che non passa da
 * `leaveGameTo`, quindi l'unica che non sostituisce la propria voce.
 *
 * `'playing'` resta fuori di proposito: una damiera ripristinata mentre si gioca
 * la governa l'instradamento della partita, e subito dopo la guardia che chiede
 * conferma prima di uscire.
 */
export function doveAtterraLaVoceDelTorneo(
  screen: string,
  tournamentStatus: string,
): 'tournament-detail' | null {
  if (screen !== 'tournament-lobby' && screen !== 'tournament-game') return null;
  return TORNEO_CHE_GIRA.has(tournamentStatus) ? null : 'tournament-detail';
}

/**
 * Si esce dalla stanza socket del torneo quando si lascia la sua pagina?
 *
 * La pagina di dettaglio esce sempre, nel cleanup del suo effetto. Per chi
 * passava di li' a curiosare e' giusto: smette di leggere una pagina e smette
 * di riceverne gli aggiornamenti.
 *
 * Per chi e' ISCRITTO no, ed e' il difetto del 18 settembre: dalla stanza
 * passano gli ANNUNCI del torneo — classifica, inizio e fine turno, fine
 * torneo — mentre gli accoppiamenti arrivano al socket per conto loro
 * (`findSocketByUserId`). Uscendone si continua a giocare senza accorgersi di
 * nulla, ma non si viene piu' avvisati che il torneo e' finito: si resta «in
 * attesa» per sempre, a partite concluse.
 *
 * Chi partecipa cambia pagina di continuo — lobby, partita, lobby — e ogni
 * smontaggio lo buttava fuori. L'ascolto deve durare quanto la partecipazione,
 * non quanto la pagina.
 */
export function siEsceDallaStanzaDelTorneo(
  tournamentId: string,
  torneoIscrittoId: string | null,
): boolean {
  return tournamentId !== torneoIscrittoId;
}
